import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, Card, CardContent, FormControl, Select, MenuItem, Dialog, DialogContent, DialogTitle } from "@mui/material";
import { dashboardIcon } from "../../config/images";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from "chart.js";
import "../Dashboard/Dashboard.css";
import AreaChart from "./AreaChart";
import translations from "../../config/language";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { Button } from "../../components/Button/button";
import { getDashboardCount, getDashboardGraphData } from "../../config/services";
import { notifyError } from "../../config/ToastMessage";
import { useSelector } from "react-redux";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard = ({ isSidebarOpen }) => {

  const language = process.env.REACT_APP_LANGUAGE;
  const [dashboardCount, setDashboardCount] = useState([]);
  const [openDatePickerFilter, setOpenDatePickerFilter] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState("Today");
  const [filterData, setFilterData] = useState(false);
  const [chartData, setChartData] = useState({});
  const [filterValues, setFilterValues] = useState({
    fromDate: null,
    toDate: null,
  });

  const { module_access } = useSelector((state) => state.userState)

  const handleFilterChange = (eventOrValue, fieldName) => {
    if (typeof eventOrValue === "object" && eventOrValue?.target) {
      // This is an event from a Select or regular input
      const { name, value } = eventOrValue.target;
      setFilterValues((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else {
      // This is a direct value from DatePicker
      if (fieldName === 'fromDate' || fieldName === 'toDate') {
        // Handle null case when date is cleared
        if (!eventOrValue) {
          setFilterValues((prev) => ({
            ...prev,
            [fieldName]: null,
          }));
          return;
        }

        const selectedDate = dayjs(eventOrValue);
        const today = dayjs();

        if (selectedDate.isAfter(today)) {
          if (fieldName === 'fromDate') {
            notifyError(translations[language].fromDateCantBeGreaterThanToday);
          } else {
            notifyError(translations[language].toDateCantBeGreaterThanToday);
          }
          return;
        }

        // Validate fromDate is not after toDate
        if (fieldName === 'fromDate' && filterValues.toDate) {
          if (selectedDate.isAfter(filterValues.toDate)) {
            notifyError(translations[language].fromDateCantBeAfterToDate);
            return;
          }
        }

        // Validate toDate is not before fromDate
        if (fieldName === 'toDate' && filterValues.fromDate) {
          if (selectedDate.isBefore(filterValues.fromDate)) {
            notifyError(translations[language].toDateCantBeBeforeFromDate);
            return;
          }
        }
      }

      setFilterValues((prev) => ({
        ...prev,
        [fieldName]: eventOrValue,
      }));
    }
  };

  const dashboardCards = [
    {
      icon: dashboardIcon.totalFarmer,
      count: dashboardCount?.registered_farmers,
      label: translations[language].totalFarmersRegistered,
      color: "#80546D",
    },
    {
      icon: dashboardIcon.totalFarmer,
      count: dashboardCount?.pending_approval_farmers,
      label: translations[language].pendingFarmerApprovals,
      color: "#D8856C",
    },
    {
      icon: dashboardIcon.totalTask,
      count: dashboardCount?.total_no_of_notifications,
      label: translations[language].taskAssignmentNotifications,
      color: "#348B81",
    },
    {
      icon: dashboardIcon.totalData,
      count: dashboardCount?.data_collectors_count,
      label: translations[language].totalDataCollectors,
      color: "#533F6F",
    },
  ];

  const handleClear = () => {
    setFilterValues({
      fromDate: null,
      toDate: null,
    });
    setFilterData(false);
  };

  const handleFilterSubmit = () => {
    setFilterData(true);
    setOpenDatePickerFilter(false);
  };

  const fetchData = async () => {
    let fromDate = filterValues.fromDate;
    let toDate = filterValues.toDate;

    if (filterData) {
      fromDate = dayjs(fromDate).format("YYYY-MM-DD");
      toDate = dayjs(toDate).format("YYYY-MM-DD");
    }
    else {
      const today = dayjs().format("YYYY-MM-DD");
      if (dateRange === "Today") {
        fromDate = today;
        toDate = today;
      } else if (dateRange === "This Week") {
        fromDate = dayjs().startOf("week").format("YYYY-MM-DD");
        toDate = dayjs().endOf("week").format("YYYY-MM-DD");
      } else if (dateRange === "This Month") {
        fromDate = dayjs().startOf("month").format("YYYY-MM-DD");
        toDate = dayjs().endOf("month").format("YYYY-MM-DD");
      }
    }

    const data = {
      start_date: fromDate,
      end_date: toDate,
      data_collector_ids: []
    }

    try {
      setLoading(true);
      const resp = await getDashboardGraphData(data);
      if (resp.data.success === true) {
        setChartData(resp.data.payload);
        setTimeout(() => {
          setLoading(false);
        }, [2000]);
      }
    } catch (error) {
      console.log(error);
      notifyError(error.response.data.message);
      setTimeout(() => {
        setLoading(false);
      }, [2000]);
    };
  };
  const fetchDashboardCount = async () => {
    try {
      const resp = await getDashboardCount();
      if (resp.data.success === true) {
        setDashboardCount(resp.data.payload);
        setTimeout(() => {
          setLoading(false);
        }, [2000]);
      }
    } catch (error) {
      console.log(error);
      notifyError(error.response.data.message);
      setTimeout(() => {
        setLoading(false);
      }, [2000]);
    };
  };

  useEffect(() => {
    fetchDashboardCount();
  }, []);

  useEffect(() => {
    fetchData();
  }, [filterData, dateRange]);
  return (
    <Box sx={{ flex: 1 }}>
      <Grid container spacing={3} sx={{ marginTop: "0px" }}>
        {dashboardCards.map((card, index) => (
          <Grid item xs={12}
            sm={isSidebarOpen == false ? 6 : 12}
            md={3}
            key={index}
            className="pt-0 grid-box"
            sx={{ padding: "12px", }}
          >
            <Card
              sx={{ backgroundColor: card.color, color: "#fff", height: "183px" }}
              className="dashboard-card"
            >
              <CardContent className="dashboard-card-content" sx={{ height: "100%" }}>
                <Box
                  display="flex"
                  alignItems="center"
                  className="ml-4 justify-content-between"
                >
                  <Box>
                    <img src={card.icon} alt="" />
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                    {card.count}
                  </Typography>
                </Box>
                <Box className="ml-3">
                  <Typography variant="h6" >
                    {card.label.split(" ")[0]}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: "bold",
                      letterSpacing: "0.5px",
                      fontSize: "18px",
                    }}
                    className="DASHBOARD-P"
                  >
                    {card.label.split(" ").slice(1).join(" ")}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box mt={4}>
        <Box className="chart-wrap">
          <Box className="d-flex justify-content-between align-items-center" style={{ flexWrap: "wrap" }}>
            <Typography variant="h6">
              {translations[language].performanceMetricsOfDataCollectors}
            </Typography>
            <Typography >
              <img src={dashboardIcon.datePicker} alt="" onClick={() => setOpenDatePickerFilter(true)} style={{ cursor: "pointer" }} />

              <FormControl size="small" sx={{ minWidth: 120 }} style={{ marginLeft: "15px" }}>
                <Select defaultValue="Today" MenuProps={{ disableScrollLock: true }} onChange={(e) => { setFilterData(false); setDateRange(e.target.value) }}>
                  <MenuItem value="Today">{translations[language].today}</MenuItem>
                  <MenuItem value="This Week">{translations[language].thisWeek}</MenuItem>
                  <MenuItem value="This Month">{translations[language].thisMonth}</MenuItem>
                </Select>
              </FormControl>
            </Typography>
          </Box>
          <Box className="mt-5">
            <div style={{ width: "100%" }}>
              <AreaChart chartData={chartData} />
            </div>
          </Box>
        </Box>
      </Box>
      <Dialog
        open={openDatePickerFilter}
        onClose={() => setOpenDatePickerFilter(false)}
        className="filter-popup"
        maxWidth="xs"
        fullWidth
        disableScrollLock
      >
        <DialogTitle style={{ padding: "10px 15px" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 0,
              mt: 0,

            }}
          >
            <Typography variant="h6" style={{ marginLeft: "5px" }}>{translations[language].filter}</Typography>
            <Typography onClick={handleClear} sx={{ color: "#666", cursor: "pointer" }}>
              {translations[language].clear}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent style={{ padding: "15px", borderTop: "1px solid #ddd", }}>
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={translations[language].fromDate}
                value={filterValues.fromDate}
                name="fromDate"
                onChange={(value) => handleFilterChange(value, "fromDate")}
                disableFuture
                maxDate={filterValues.toDate}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={translations[language].toDate}
                value={filterValues.toDate}
                name="toDate"
                onChange={(value) => handleFilterChange(value, "toDate")}
                disableFuture
                minDate={filterValues.fromDate}
              />
            </LocalizationProvider>
            <Button
              title={translations[language].submit}
              onClick={handleFilterSubmit}
              classname={"assign-task-button"}
              loading={loading}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
    // </Box>
  )
};

export default Dashboard;
